import React, {
  createRef, useEffect,
} from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useOutletContext,
} from 'react-router-dom';
import { AiOutlineEye, AiOutlineEyeInvisible, AiOutlineInfoCircle } from 'react-icons/ai';
import { useSelector, useDispatch } from 'react-redux';
// import { RiDeleteBin6Line } from 'react-icons/ri';
import { useTranslation } from 'react-i18next';
import dots from '../../../assets/images/dots.svg';
import withModalConfirm from '../../../lib/withModalConfirm';

import {
  createIndicator,
  deleteIndicator,
} from '../../../actions/indicators';

import {
  // Textarea,
  ErrorField,
  StyledSelect,
} from '../../../lib/HooksFormFields';

import styles from './indicators.module.scss';
import PositionalModal from '../../../lib/PositionalModal';
import { updateDashboardData } from '../../../actions/dashboards';
import { INDICATOR_RESET } from '../../../actions/types';

const ActionsBtn = ({
  _id,
  name,
  confirm,
  previewIndicator,
  duplicateIndicator,
}) => {
  const dispatch = useDispatch();
  // const navigate = useNavigate();
  const { t } = useTranslation();
  // const { search: searchQuery } = useLocation();
  const cellRef = createRef(`dashboard-${_id}`);
  const modalRef = createRef(`dashboard-modal-${_id}`);

  function openModal(e) {
    e.preventDefault();
    e.stopPropagation();
    modalRef.current.open();
  }

  function handleDeleteIndicator(e) {
    e.preventDefault();
    e.stopPropagation();
    confirm(
      () => deleteIndicator(dispatch, _id),
      `${t('dashboard.indicator.delete')} : ${name} ?`,
    );
  }
  function handleDuplicateIndicator(e) {
    e.preventDefault();
    e.stopPropagation();
    duplicateIndicator();
    modalRef.current.close();
    // duplicateDashboard(dispatch, _id);
  }

  function handlePreviewIndicator(e) {
    e.preventDefault();
    e.stopPropagation();
    previewIndicator(_id);
  }

  return (
    <div className={styles.action}>
      <PositionalModal
        ref={modalRef}
        parent={cellRef}
        alignmentX={'right'}
        alignmentY={'middle'}
      >
        <div className={styles.containerActions}>
          <div><p onClick={(e) => handlePreviewIndicator(e)}>{t('btn.preview')}</p></div>
          <div><p onClick={(e) => handleDuplicateIndicator(e)}>{t('btn.copy')}</p></div>
          <div><p onClick={(e) => handleDeleteIndicator(e)}>{t('btn.delete')}</p></div>
        </div>
      </PositionalModal>
      <div className={styles.toggle}>
        <span onClick={openModal}>
          <img src={dots} alt={`action sur l indicateur: ${name}`} />
        </span>
      </div>
      <div ref={cellRef} className={styles.positionModal}></div>
    </div>
  );
};

const Indicators = ({ confirm }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { dashboardReducer, indicatorReducer } = useSelector((store) => store);
  const {
    dashboard, user, updateErrors,
  } = dashboardReducer;
  const { indicators, indicatorsDataUpdating } = indicatorReducer;
  const {
    control, errors, watch, setValue,
    effectiveIndicators, setDraftIndicatorChanges,
  } = useOutletContext();
  const { search } = useLocation();
  const { dashboardId } = useParams();

  const openIndicator = (id) => {
    navigate(`/edit-indicator/${dashboardId}/${id}/informations${search}`);
  };

  const previewIndicator = (id) => {
    navigate(`/edit-indicator/${dashboardId}/${id}/custom/preview${search}`);
  };

  const duplicateIndicator = async (indictor) => {
    const data = {
      ...indictor,
      name: `${indictor.name} (copie)`,
    };
    delete data._id;
    delete data.createdAt;
    delete data.updatedAt;
    await createIndicator(dispatch, {
      ...data,
      position: indicators.length + 1,
    });
  };

  const api = watch('api');
  const dataset = watch('dataset');

  const isNewKey = dataset?.value !== dashboard?.key;

  function handleIndicatorStatus(e, status, id) {
    e.stopPropagation();
    // Update draft state instead of immediately saving
    setDraftIndicatorChanges((prev) => ({
      ...prev,
      [id]: { ...prev[id], isPublished: !status },
    }));
  }

  function handleCreateIndicator() {
    navigate(`/edit-indicator/${dashboardId}/informations${search}`);
    dispatch({
      type: INDICATOR_RESET,
    });
  }

  useEffect(() => {
    if (api?.value) {
      setValue('requests', api.value);
    }
  }, [api]);

  useEffect(() => {
    if (dataset?.value) {
      setValue('key', dataset.value);
    }
  }, [dataset]);

  function handleUpdateDashboardData() {
    let editorName = '';
    if (user?.user?.firstName?.value && user?.user?.lastName?.value) {
      editorName = ''.concat(user.user.firstName.value, ' ', user.user.lastName.value);
    }
    // console.debug('Using the new intermediary method handleUpdateDashboardData with editor',
    //  editorName);
    updateDashboardData(dispatch, dataset, dashboard, isNewKey, editorName);
  }

  return (
    <div className={styles.indicators}>
      <div>
        <p>{t('dashboard.indicator.label')}</p>
        <div className={styles.btns}>
        <button className='secondary' onClick={() => handleCreateIndicator()}>{t('dashboard.indicator.create')}</button>
        {!isNewKey
          && api?.value
          && indicators.length > 0
          && !indicatorsDataUpdating
          && <button
            type="button"
            // onClick={() => updateDashboardData(dispatch, dataset, dashboard, isNewKey)}
            onClick={() => handleUpdateDashboardData()}
          >
            {t('dashboard.updateIndicators')}
          </button>
        }
        </div>
        {(indicators.length > 0) && (
          <ul className={styles.listIndicators}>
            {effectiveIndicators.sort(
              (a, b) => (b.position < a.position ? 1 : -1),
            ).map((d) => {
              // Check if this indicator has any draft changes (isPublished or size)
              // We need to check if the effective value is different from the original value
              // BUT, effectiveIndicators already HAS the changes.
              // To enable "draftChanged" style, we'd need access to draftIndicatorChanges
              // to see if this specific ID has an entry.
              // For now, let's keep it simple or we can re-expose draftIndicatorChanges
              // if needed for styling.
              // Actually, simpler: if effective !== original
              const original = indicators.find((i) => i._id === d._id);
              const hasDraftChange = original
                && (original.isPublished !== d.isPublished || original.size !== d.size);

              return (
              <li key={`indicator-${d._id}`}>
                <div
                  className={`${styles.indicator} ${d.isPublished ? styles.active : ''} ${hasDraftChange ? styles.draftChanged : ''}`}
                  onClick={() => openIndicator(d._id)}
                >
                  <p>{d.name} {d?.subject && (
                    <AiOutlineInfoCircle title={d.subject}/>
                  )}</p>

                  <div className={styles.actions}>
                    <button
                      type="button"
                      onClick={(e) => handleIndicatorStatus(e, d.isPublished, d._id)}
                      className={`${styles.pen}`}
                    >
                    {d.isPublished ? <AiOutlineEye/> : <AiOutlineEyeInvisible />}
                    </button>

                    <ActionsBtn
                      name={d.name}
                      _id={d._id}
                      confirm={confirm}
                      duplicateIndicator={() => duplicateIndicator(d)}
                      previewIndicator={previewIndicator}
                    />
                  </div>
                </div>
              </li>
              );
            })
          }
          </ul>
        )}
      </div>
      <div>
        {dashboard && (
          <>
           <div className={styles.containerField}>
              <StyledSelect
                name="api"
                control={control}
                label={t('dashboard.api')}
                isSearchable
                options={user?.apis}
              />

              {errors?.requests?.message && <ErrorField message={errors.requests.message} />}
            </div>
            <div className={styles.containerField}>
              <div>
                <StyledSelect
                  name="dataset"
                  control={control}
                  label={t('dashboard.dataset')}
                  isSearchable
                  options={user?.keys.filter((k) => k.api === api?.value) || []}
                />
              </div>
              {errors?.key?.message && <ErrorField message={errors.key.message} />}
            </div>
            {indicatorsDataUpdating
              && <div className={styles.currentlyUpdating}>{t('indicator.currentlyUpdating')}</div>
            }
            {!indicatorsDataUpdating
              && updateErrors?.length > 0
              && <div className={styles.errors}>
                {updateErrors.map((e, i) => {
                  const key = `${e.indicator}-${i}`;

                  if (e.value === undefined) {
                    return (<ErrorField
                      key={key}
                      message={t('dashboard.resErrors', { indicator: e.indicator })} />);
                  }
                  return (<ErrorField
                    key={key}
                    message={t('dashboard.errors', { indicator: e.indicator, value: e.value?.value || e.value })} />);
                })}
              </div>
            }
            {isNewKey
              && api?.value
              && indicators.length > 0
              && !indicatorsDataUpdating
              && <button
                type="button"
                // onClick={() => updateDashboardData(dispatch, dataset, dashboard, isNewKey)}
                onClick={() => handleUpdateDashboardData()}
              >
                {t('dashboard.update')}
              </button>
            }
          </>
        )}
      </div>
    </div>
  );
};

export default withModalConfirm(Indicators);
